import { useMemo, useState } from "react";
import { ChoiceField } from "@/components/tool/CalcFields";
import { type Harmony, hexToRgb, palette } from "@/lib/design/color";
import { m } from "@/paraglide/messages";

const HARMONIES: Harmony[] = [
  "complementary",
  "analogous",
  "triadic",
  "tetradic",
  "monochromatic",
];

export function PaletteGenerator() {
  const [base, setBase] = useState("#3366cc");
  const [harmony, setHarmony] = useState<Harmony>("triadic");
  const [copied, setCopied] = useState<string | null>(null);

  const colors = useMemo(
    () => (hexToRgb(base) ? palette(base, harmony) : []),
    [base, harmony],
  );

  const copy = (hex: string) => {
    navigator.clipboard?.writeText(hex).then(
      () => setCopied(hex),
      () => {},
    );
  };

  return (
    <div className="sci-card rounded-2xl space-y-5 p-6 sm:p-8">
      <div className="space-y-1.5">
        <span className="block text-sm text-muted-foreground">
          {m.cp_base()}
        </span>
        <div className="flex items-center gap-2">
          <input
            type="color"
            aria-label={m.cp_base()}
            value={hexToRgb(base) ? base : "#000000"}
            onChange={(e) => setBase(e.target.value)}
            className="h-10 w-12 shrink-0 cursor-pointer rounded-md border border-black/15 bg-transparent"
          />
          <input
            type="text"
            aria-label={`${m.cp_base()} hex value`}
            value={base}
            onChange={(e) => setBase(e.target.value)}
            spellCheck={false}
            className="w-full rounded-md border border-black/15 bg-white px-3 py-2 font-mono text-sm text-neutral-900"
          />
        </div>
      </div>

      <ChoiceField
        label={m.cp_harmony()}
        value={harmony}
        onChange={setHarmony}
        options={HARMONIES.map((h) => ({
          value: h,
          label: m[`cp_${h}` as "cp_triadic"](),
        }))}
      />

      {colors.length ? (
        <div
          data-testid="cp-swatches"
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${colors.length}, minmax(0, 1fr))`,
          }}
        >
          {colors.map((hex, i) => (
            <button
              type="button"
              key={`${hex}-${i}`}
              onClick={() => copy(hex)}
              className="group flex flex-col overflow-hidden rounded-xl border border-black/10 text-left"
            >
              <span className="h-24 w-full" style={{ backgroundColor: hex }} />
              <span className="flex items-center justify-between px-2 py-1.5 font-mono text-xs">
                <span className="uppercase">{hex}</span>
                <span className="text-muted-foreground">
                  {copied === hex ? m.cp_copied() : m.cp_copy()}
                </span>
              </span>
            </button>
          ))}
        </div>
      ) : (
        <p className="text-sm text-muted-foreground">{m.cp_hint()}</p>
      )}
    </div>
  );
}
